import { useCallback, useEffect, useState } from "react";
import { api, esquecerLoginDoPortal, guardarToken, lerToken, type Colaborador } from "./api";
import Login from "./components/Login";
import TrocarSenha from "./components/TrocarSenha";
import Inicio from "./components/Inicio";
import BaterPonto from "./components/BaterPonto";
import MeuEspelho from "./components/MeuEspelho";
import MinhasFolhas from "./components/MinhasFolhas";
import HorasExtras from "./components/HorasExtras";
import MeuBanco from "./components/MeuBanco";
import Documentos from "./components/Documentos";
import MeusDados from "./components/MeusDados";
import { ICONES, tituloDe, type Secao } from "./secoes";
import {
  ACESSO_COMPLETO,
  ativarModoLeitura,
  podeVerAba,
  primeiraAbaLiberada,
  visualizacaoDaResposta,
  type Visualizacao,
} from "./modo-leitura";

/**
 * COLABORADORES — o sistema de todo colaborador da SERCAMP.
 *
 * Navegação pensada para o celular: a barra de baixo tem as quatro coisas mais
 * usadas (Início, Ponto, Documentos, Meus dados), e o Início leva a todas as
 * outras. Cada seção interna tem "voltar" no topo — no celular não existe menu
 * lateral para a pessoa se achar.
 *
 * A seção aberta fica no endereço (#folhas, #documentos…): o botão "voltar" do
 * celular funciona como a pessoa espera, e um link direto para as folhas pode
 * ir no e-mail do fechamento.
 *
 * A sessão é retomada sozinha pelo token guardado: exigir CPF e senha quatro
 * vezes por dia faria as pessoas anotarem a senha no capacete.
 */

const VALIDAS: Secao[] = ["inicio", "bater", "espelho", "folhas", "extras", "banco", "documentos", "dados"];

function secaoDoEndereco(): Secao {
  const h = window.location.hash.replace(/^#/, "") as Secao;
  return VALIDAS.includes(h) ? h : "inicio";
}

const BARRA: Secao[] = ["inicio", "bater", "documentos", "dados"];
const ROTULO_BARRA: Partial<Record<Secao, string>> = {
  inicio: "Início",
  bater: "Ponto",
  documentos: "Documentos",
  dados: "Meus dados",
};

export default function App() {
  const [colaborador, setColaborador] = useState<Colaborador | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [secao, setSecao] = useState<Secao>(secaoDoEndereco);
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  /* Somente visualização (cadastro do Portal — ver server/visualizacao.ts):
     as seções liberadas e nada de gravar. `visPronta` segura a tela até a
     resposta: sem isso, quem chegasse por #bater veria a câmera abrir antes
     de a seção ser escondida. */
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);
  const [visPronta, setVisPronta] = useState(false);

  /** Retoma a sessão guardada, se ainda valer. */
  const retomar = useCallback(async () => {
    if (!lerToken()) {
      setCarregando(false);
      return;
    }
    try {
      const { colaborador } = await api.me();
      setColaborador(colaborador);
    } catch {
      // Token vencido ou acesso desativado: volta ao login sem alarde.
      guardarToken(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    retomar();
  }, [retomar]);

  // Quem entrou: o que o Portal libera para ela aqui. Falhou = acesso completo
  // na tela (o servidor continua travando se o Portal disser o contrário).
  const colaboradorId = colaborador?.id ?? null;
  useEffect(() => {
    setVis(ACESSO_COMPLETO);
    setVisPronta(false);
    if (colaboradorId === null) return;
    let vivo = true;
    api
      .visualizacao()
      .then((r) => vivo && setVis(visualizacaoDaResposta(r)))
      .catch(() => {})
      .finally(() => vivo && setVisPronta(true));
    return () => {
      vivo = false;
    };
  }, [colaboradorId]);

  // A faixa e o esconde-botões só na tela principal: a troca de senha fica
  // livre (a provisória OBRIGA a troca — travar prenderia a pessoa ali).
  const telaPrincipal = !!colaborador && !colaborador.precisa_trocar && !trocandoSenha && visPronta;
  useEffect(() => {
    ativarModoLeitura(telaPrincipal && vis.somenteLeitura);
  }, [telaPrincipal, vis.somenteLeitura]);

  // Seção fora das liberadas (inclusive por link direto, #bater) cai na primeira liberada.
  const verSecao = (s: Secao) => podeVerAba(vis, s);
  useEffect(() => {
    if (!visPronta || verSecao(secao)) return;
    const primeira = primeiraAbaLiberada(vis, VALIDAS) as Secao | null;
    if (primeira) {
      window.location.hash = primeira === "inicio" ? "" : primeira;
      setSecao(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secao, vis, visPronta]);

  // O "voltar" do celular muda o endereço; a tela acompanha.
  useEffect(() => {
    const aoMudar = () => setSecao(secaoDoEndereco());
    window.addEventListener("hashchange", aoMudar);
    return () => window.removeEventListener("hashchange", aoMudar);
  }, []);

  function abrir(nova: Secao) {
    if (nova === secao || !verSecao(nova)) return;
    window.location.hash = nova === "inicio" ? "" : nova;
    setSecao(nova);
    window.scrollTo(0, 0);
  }

  function sair() {
    guardarToken(null);
    esquecerLoginDoPortal();
    setColaborador(null);
    window.location.hash = "";
    setSecao("inicio");
  }

  if (carregando) {
    return <div className="carregando carregando-cheio">Abrindo…</div>;
  }

  if (!colaborador) {
    return <Login onEntrou={setColaborador} />;
  }

  // Primeiro acesso: a senha provisória não pode continuar valendo. Esta tela
  // vem ANTES de qualquer outra e não tem saída — ver TrocarSenha.
  if (colaborador.precisa_trocar) {
    return (
      <TrocarSenha
        obrigatoria
        onTrocada={() => setColaborador({ ...colaborador, precisa_trocar: false })}
      />
    );
  }

  if (trocandoSenha) {
    return (
      <TrocarSenha
        obrigatoria={false}
        onTrocada={() => setTrocandoSenha(false)}
        onCancelar={() => setTrocandoSenha(false)}
      />
    );
  }

  // Até o Portal responder, nenhuma seção monta (ver `visPronta`).
  if (!visPronta) {
    return <div className="carregando carregando-cheio">Abrindo…</div>;
  }

  // Sem o Início liberado, o "voltar" levaria a lugar nenhum: fica o cabeçalho normal.
  const interna = !BARRA.includes(secao) && verSecao("inicio");
  // Seção não liberada não monta nada (o efeito acima já troca de seção).
  const aberta = verSecao(secao) ? secao : null;

  return (
    <div className="app">
      <header className="cabecalho">
        {interna ? (
          <button className="cabecalho-voltar" onClick={() => abrir("inicio")}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{tituloDe(secao)}</span>
          </button>
        ) : (
          <div className="cabecalho-identidade">
            <span className="cabecalho-marca">Colaboradores SERCAMP</span>
            <span className="cabecalho-nome">{colaborador.nome}</span>
          </div>
        )}
        <div className="cabecalho-acoes">
          <button className="botao-texto" onClick={() => setTrocandoSenha(true)}>
            Senha
          </button>
          <button className="botao-texto" onClick={sair}>
            Sair
          </button>
        </div>
      </header>

      <main className="conteudo">
        {!interna && secao !== "inicio" && <h1 className="titulo-pagina">{tituloDe(secao)}</h1>}
        {aberta === "inicio" && <Inicio colaborador={colaborador} onAbrir={abrir} podeAbrir={verSecao} />}
        {aberta === "bater" && <BaterPonto nome={colaborador.nome} />}
        {aberta === "espelho" && <MeuEspelho />}
        {aberta === "folhas" && <MinhasFolhas />}
        {aberta === "extras" && <HorasExtras />}
        {aberta === "banco" && <MeuBanco />}
        {aberta === "documentos" && <Documentos />}
        {aberta === "dados" && <MeusDados />}
      </main>

      <nav className="abas">
        {BARRA.filter(verSecao).map((id) => (
          <button
            key={id}
            className={`aba ${secao === id ? "aba-ativa" : ""}`}
            onClick={() => abrir(id)}
          >
            {ICONES[id]}
            <span>{ROTULO_BARRA[id]}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
